/**
 * Revenue Control Center API Client
 *
 * API hooks for Revenue Control Center dashboard
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  RevenueOverview,
  FunnelPerformance,
  ReliabilityRevenueImpact,
} from '@forgenetwork/types';

/**
 * Revenue Control Center Dashboard Data
 */
export interface RevenueControlCenterData {
  revenueOverview?: RevenueOverview;
  funnelPerformance?: FunnelPerformance;
  reliabilityRevenueImpact?: ReliabilityRevenueImpact;
  pricingIntelligence?: {
    recommendations?: Array<{
      planId: string;
      currentPrice: number;
      optimalPrice: number;
      recommendation: string;
      confidence: number;
    }>;
    elasticityCurves?: Array<{
      planId: string;
      curve: Array<{ price: number; demand: number }>;
    }>;
    competitorComparison?: Array<{
      planId: string;
      competitors: Array<{ name: string; price: number }>;
    }>;
  };
  salesAutomationControl?: {
    dealAccelerationTriggers?: number;
    proposalQueue?: number;
    highIntentLeads?: number;
    salesAutopilotMetrics?: {
      sent: number;
      opens: number;
      replies: number;
    };
    founderInvolvementRecommendations?: Array<{
      leadId: string;
      reason: string;
      priority: 'high' | 'medium' | 'low';
    }>;
  };
  marketingAutomationInsights?: {
    topPerformingMessages?: Array<{
      id: string;
      title: string;
      performance: {
        views?: number;
        conversions?: number;
        roi?: number;
      };
    }>;
    contentRevenueAttribution?: Array<{
      contentId: string;
      revenue: number;
    }>;
  };
  customerSuccessHealth?: {
    expansionPotentialAlerts?: number;
    atRiskAccounts?: number;
    workflowReliabilityPatterns?: Array<{
      userId: string;
      reliabilityScore: number;
      pattern: string;
    }>;
    timeToValueMetrics?: {
      average: number;
      byIcp: Array<{ icp: string; average: number }>;
    };
  };
  boardSummaries?: {
    quarterlyBoardPack?: {
      revenue: { arr: number; growth: number };
      customers: { total: number; enterprise: number };
      reliability: { score: number; uptime: number };
      governance: { soc2Status: string; aiActCompliance: boolean };
    };
    enterpriseReadinessScore?: number;
    governancePosture?: { soc2: string; aiAct: string; nist: string };
  };
}

/**
 * Get complete Revenue Control Center dashboard
 */
export function useRevenueControlCenter() {
  return useQuery({
    queryKey: ['revenue-control-center'],
    queryFn: async (): Promise<RevenueControlCenterData> => {
      return api.get<RevenueControlCenterData>('/revenue-cmo-partner/revenue-control-center');
    },
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000, // Consider stale after 30 seconds
  });
}

/**
 * Get Revenue Overview (Section 1)
 */
export function useRevenueOverview() {
  return useQuery({
    queryKey: ['revenue-overview'],
    queryFn: async (): Promise<RevenueOverview> => {
      return api.get<RevenueOverview>('/revenue-cmo-partner/revenue-control-center/revenue-overview');
    },
    refetchInterval: 60000,
  });
}

/**
 * Get Funnel Performance (Section 2)
 */
export function useFunnelPerformance(timeframeDays = 30) {
  return useQuery({
    queryKey: ['funnel-performance', timeframeDays],
    queryFn: async (): Promise<FunnelPerformance> => {
      return api.get<FunnelPerformance>(
        `/revenue-cmo-partner/revenue-control-center/funnel-performance?timeframeDays=${timeframeDays}`
      );
    },
    refetchInterval: 60000,
  });
}

/**
 * Get Reliability Revenue Impact (Section 3)
 */
export function useReliabilityRevenueImpact(timeframeDays = 30) {
  return useQuery({
    queryKey: ['reliability-revenue-impact', timeframeDays],
    queryFn: async (): Promise<ReliabilityRevenueImpact> => {
      return api.get<ReliabilityRevenueImpact>(
        `/revenue-cmo-partner/revenue-control-center/reliability-impact?timeframeDays=${timeframeDays}`
      );
    },
    refetchInterval: 60000,
  });
}

/**
 * Get Pricing Intelligence (Section 4)
 */
export function usePricingIntelligence() {
  return useQuery({
    queryKey: ['pricing-intelligence'],
    queryFn: async (): Promise<RevenueControlCenterData['pricingIntelligence']> => {
      return api.get<RevenueControlCenterData['pricingIntelligence']>('/revenue-cmo-partner/revenue-control-center/pricing-intelligence');
    },
    refetchInterval: 120000, // Refresh every 2 minutes (pricing changes less frequently)
  });
}

/**
 * Get Sales Automation Control (Section 5)
 */
export function useSalesAutomationControl() {
  return useQuery({
    queryKey: ['sales-automation-control'],
    queryFn: async () => {
      return api.get('/revenue-cmo-partner/revenue-control-center/sales-automation');
    },
    refetchInterval: 30000, // Refresh every 30 seconds (more frequent for sales activity)
  });
}

/**
 * Get Marketing Automation Insights (Section 6)
 */
export function useMarketingAutomationInsights() {
  return useQuery({
    queryKey: ['marketing-automation-insights'],
    queryFn: async () => {
      return api.get('/revenue-cmo-partner/revenue-control-center/marketing-insights');
    },
    refetchInterval: 60000,
  });
}

/**
 * Get Customer Success Health (Section 7)
 */
export function useCustomerSuccessHealth() {
  return useQuery({
    queryKey: ['customer-success-health'],
    queryFn: async () => {
      return api.get('/revenue-cmo-partner/revenue-control-center/customer-success');
    },
    refetchInterval: 60000,
  });
}

/**
 * Get Board Summaries (Section 8)
 */
export function useBoardSummaries() {
  return useQuery({
    queryKey: ['board-summaries'],
    queryFn: async () => {
      return api.get('/revenue-cmo-partner/revenue-control-center/board-summaries');
    },
    refetchInterval: 300000, // Refresh every 5 minutes (board data changes less frequently)
  });
}

/**
 * Refresh Revenue Control Center data
 */
export function useRefreshRevenueControlCenter() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: ['revenue-control-center'] });
      await queryClient.invalidateQueries({ queryKey: ['revenue-overview'] });
      await queryClient.invalidateQueries({ queryKey: ['funnel-performance'] });
      await queryClient.invalidateQueries({ queryKey: ['reliability-revenue-impact'] });
      await queryClient.invalidateQueries({ queryKey: ['pricing-intelligence'] });
      await queryClient.invalidateQueries({ queryKey: ['sales-automation-control'] });
      await queryClient.invalidateQueries({ queryKey: ['marketing-automation-insights'] });
      await queryClient.invalidateQueries({ queryKey: ['customer-success-health'] });
      await queryClient.invalidateQueries({ queryKey: ['board-summaries'] });
    },
  });
}
